import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { useLocation, useNavigate } from 'react-router-dom';
import PencomTextField from '@/components/input/PencomTextField';
import { toast } from '@/utils/toast';
import { useForm } from '@tanstack/react-form';
import { type OTPVerificationForm } from '@/forms/auth/auth.form';
import { useResendAuthorizedContactVerificationOTP, useVerifyAuthorizedContactVerificationOTP } from '@/hooks/auth/auth.hook';
import { PencomRoutes } from '@/router/PencomRoutes';
import { useOTPRateLimiter } from '@/hooks/useOTPRateLimiter';
// import { PencomBackButton } from '@/components/layout/PencomBackButton';

const formatResendTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const EmployerRepVerificationPage = () => {
    const navigate = useNavigate();
    const data = useLocation().state as Record<string, string> | null;

    const verifyAuthorizedContactVerificationOTPMutation = useVerifyAuthorizedContactVerificationOTP();
    const sendVerificationOTPMutation = useResendAuthorizedContactVerificationOTP();
    const { canResend, timeRemaining, startCooldown } = useOTPRateLimiter(60);

    const contactEmail = data?.email as string | undefined;
    const employerCode = data?.employerCode;
    const emailDisplay = contactEmail || 'your email';

    const form = useForm({
        defaultValues: {
            otp: '',
        } as OTPVerificationForm,
        onSubmit: async ({ value }) => {
            if (!employerCode) return;
            verifyAuthorizedContactVerificationOTPMutation.mutate(
                { employerCode, otpCode: value.otp },
                {
                    onSuccess: (response) => {
                        toast.success('Contact person verified successfully');
                        navigate(PencomRoutes.ONBOARDING_COMPLETION, { state: response });
                    },
                    onError: (error) => {
                        form.setFieldValue('otp', '');
                        toast.error(error.message || 'Invalid or expired code. Please try again.');
                    },
                }
            );
        },
    });

    const handleResendOTP = () => {
        if (!canResend || !employerCode) return;
        sendVerificationOTPMutation.mutate(employerCode, {
            onSuccess: () => {
                toast.success('OTP sent successfully');
                startCooldown();
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to send OTP. Please try again.');
            },
        });
    };

    return (
        <div className="w-full flex flex-col">
            {/* <PencomBackButton title="Back" /> */}

            <h2 className="font-clash-display font-semibold text-[40px] text-medium-dark-grey leading-[1.25] tracking-[-0.6px] mt-4">
                Confirm email address
            </h2>
            <p className="font-clash-display text-center text-medium-dark-grey leading-[25px] tracking-[-0.08px] mt-3">
                Enter the 6-digit code sent to <span className="font-semibold">{emailDisplay}</span>
            </p>

            <div className="flex flex-col gap-10 mt-10">
                <form.Field
                    name="otp"
                    validators={{
                        onChange: ({ value }) => {
                            if (value && value.length > 0 && value.length !== 6) return 'OTP must be 6 digits';
                            return undefined;
                        },
                    }}
                    children={(field) => (
                        <div className="flex flex-col gap-2">
                            <PencomTextField
                                id="verification-code"
                                label=""
                                type="otp"
                                value={field.state.value}
                                onChange={(e) => {
                                    if (verifyAuthorizedContactVerificationOTPMutation.error) verifyAuthorizedContactVerificationOTPMutation.reset();
                                    if (sendVerificationOTPMutation.error) sendVerificationOTPMutation.reset();
                                    field.handleChange(e.target.value);
                                }}
                                otpLength={6}
                                otpMasked={true}
                                otpMaskChar="•"
                                otpMaskDelay={1500}
                                error={
                                    field.state.meta.errors.length > 0 ||
                                    !!verifyAuthorizedContactVerificationOTPMutation.error
                                }
                                helperText={
                                    field.state.meta.errors.length > 0
                                        ? (field.state.meta.errors[0] as string)
                                        : verifyAuthorizedContactVerificationOTPMutation.error
                                            ? 'Invalid or expired code. Please try again.'
                                            : undefined
                                }
                            />
                            {field.state.meta.errors.length > 0 && !verifyAuthorizedContactVerificationOTPMutation.error && (
                                <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600 font-clash-display">{field.state.meta.errors[0]}</p>
                                </div>
                            )}
                        </div>
                    )}
                />

                {(verifyAuthorizedContactVerificationOTPMutation.error || sendVerificationOTPMutation.error) && (
                    <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600 font-clash-display">
                            {verifyAuthorizedContactVerificationOTPMutation.error
                                ? 'Invalid or expired code. Please try again.'
                                : sendVerificationOTPMutation.error?.message ||
                                'Something went wrong. Please try again.'}
                        </p>
                    </div>
                )}

                <form.Subscribe
                    children={(state) => {
                        const isDisabled =
                            !state.values.otp ||
                            state.values.otp.length !== 6 ||
                            verifyAuthorizedContactVerificationOTPMutation.isPending;
                        return (
                            <PrimaryButton
                                className="w-full"
                                title={verifyAuthorizedContactVerificationOTPMutation.isPending ? 'Verifying...' : 'Verify Email'}
                                onClick={() => form.handleSubmit()}
                                disabled={isDisabled}
                                isLoading={verifyAuthorizedContactVerificationOTPMutation.isPending}
                            />
                        );
                    }}
                />

                <div className="bg-white border border-stroke rounded-lg p-4 flex flex-col gap-2.5">
                    <div className="flex items-center gap-2">
                        <img src="/assets/icons/info-outline.svg" alt="" className="w-4 h-4 shrink-0" aria-hidden />
                        <p className="font-clash-display font-medium text-sm text-medium-dark-grey leading-[18px] tracking-[-0.07px]">
                            Didn&apos;t get code?
                        </p>
                    </div>
                    <p className="font-clash-display font-normal text-sm text-medium-grey leading-[18px] tracking-[-0.07px]">
                        Please check your SPAM or JUNK folder if you don&apos;t see it. <br /> You can request a new code below.
                    </p>
                    <p className="font-clash-display font-normal text-xs text-medium-dark-grey leading-[25px] tracking-[-0.06px]">
                        {canResend && !sendVerificationOTPMutation.isPending ? (
                            <button
                                type="button"
                                onClick={handleResendOTP}
                                className="text-primary-button font-semibold hover:underline cursor-pointer"
                            >
                                Resend the code
                            </button>
                        ) : (
                            <>Resend the code in {formatResendTimer(timeRemaining)}</>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EmployerRepVerificationPage;
